import path from 'path';

import analytics from '@repo/analytics';
import { DOWNLOAD_SAP } from '@repo/editor/src/global/events';
import { fireToast } from '@repo/ui/components/Toast';
import { create } from 'zustand';

import {
  buildImageProxyUrlByObjectName,
  removeBackground,
  uploadImage,
  downloadFile,
} from 'components/AiTools/utils';
import { VectorizerOptions } from 'components/Vectorizer/Settings/Settings';
import {
  VECTORIZER_BACKGROUND_REMOVE,
  VECTORIZER_VECTORIZE,
} from 'global/analyticsEvents';
import SapDownloadPayload from 'global/types/Analytics';
import { VectorizeInfo, VectorizingSolver } from 'global/types/Generators';
import { getImageDimensions } from 'global/utils/fileUtils';

import generatorsApi, {
  GeneratedContentStatus,
} from '../../global/generatorsApi';

import { State } from './types';

const VECTORIZING_REQUEST_INTERVAL = 3000;

// TODO: Remove during Old Vectorizer Cleansing
// @deprecated
export const useVectorizerStore = create<State>((set, get) => ({
  loading: false,
  vectorizing: false,
  image: null,
  displayedImage: null,
  result: null,
  vectorized: false,
  fileName: null,
  dimensions: null,
  backgroundRemoved: false,
  actions: {
    selectFile: async (file: File): Promise<void> => {
      const localUrl = URL.createObjectURL(file);
      set({ loading: true, image: localUrl });
      const dimensions = await getImageDimensions(localUrl);
      URL.revokeObjectURL(localUrl);
      const uploadResult = await uploadImage(
        file,
        dimensions.width,
        dimensions.height,
        undefined,
        true
      );

      if (uploadResult.error) {
        fireToast({
          label: uploadResult.error,
          error: true,
          duration: 5000,
        });
        set({ loading: false, image: null });
        return;
      }

      const filename = path.parse(file.name);

      set({
        loading: false,
        image: uploadResult.url,
        displayedImage: null,
        result: null,
        fileName: filename.name,
        dimensions: dimensions,
        vectorized: false,
        backgroundRemoved: false,
      });
    },
    reset: (): void => {
      set({
        loading: false,
        displayedImage: null,
        result: null,
        vectorized: false,
        vectorizing: false,
        backgroundRemoved: false,
      });
    },
    removeBackground: async (): Promise<void> => {
      set({ loading: true });
      const { image, dimensions } = get();
      if (!image || !dimensions) {
        fireToast({
          label: 'Source image not found',
          error: true,
          duration: 5000,
        });
        set({ loading: false });
        return;
      }
      const removeBackgroundResult = await removeBackground(
        image,
        dimensions.width,
        dimensions.height
      );
      if (removeBackgroundResult.error) {
        fireToast({
          label: removeBackgroundResult.error,
          error: true,
          duration: 5000,
        });

        set({ loading: false });
        return;
      }

      void analytics.track(VECTORIZER_BACKGROUND_REMOVE);
      set({
        loading: false,
        displayedImage: removeBackgroundResult.url,
        vectorized: false,
        backgroundRemoved: true,
      });
    },
    vectorize: async (
      options: VectorizerOptions,
      colorCounter: number
    ): Promise<void> => {
      const solver =
        colorCounter === 1 ? VectorizingSolver.binary : VectorizingSolver.color;

      const colorsNumber = options === 'blackWhite' ? 1 : colorCounter;

      set({ loading: true, vectorizing: true });
      const { image, displayedImage, dimensions } = get();

      if ((!image && !displayedImage) || !dimensions) {
        fireToast({
          label: 'Image is not found',
          error: true,
          duration: 5000,
        });

        set({ loading: false, vectorizing: false });
        return;
      }

      const handleFail = (error?: string): void => {
        if (!error || error !== 'NOT_ENOUGH_CREDITS') {
          fireToast({
            label: error,
            error: true,
            duration: 5000,
          });
        }

        set({ loading: false, vectorizing: false });
      };

      const handleSuccess = (successResponse: VectorizeInfo): void => {
        const url = buildImageProxyUrlByObjectName(
          successResponse.objectName,
          successResponse.info.image_width,
          successResponse.info.image_height
        );

        void analytics.track(VECTORIZER_VECTORIZE, {
          color: options,
        });

        set({
          loading: false,
          vectorizing: false,
          displayedImage: url,
          result: {
            id: successResponse.id,
            objectName: successResponse.objectName,
            width: successResponse.info.image_width,
            height: successResponse.info.image_height,
          },
          vectorized: true,
        });
      };

      const handlePolling = async (id: string): Promise<void> => {
        let response;
        do {
          await new Promise((resolve) =>
            setTimeout(resolve, VECTORIZING_REQUEST_INTERVAL)
          );
          response = await generatorsApi.showVectorizedImage(id);
        } while (
          response.success &&
          response.status === GeneratedContentStatus.Started
        );

        if (
          response.success &&
          response.status === GeneratedContentStatus.Complete
        ) {
          return handleSuccess(response);
        }

        handleFail(response.error);
      };

      const vectorizingResponse = await generatorsApi.vectorize(
        displayedImage ?? image!,
        solver,
        colorsNumber,
        [0, 0, dimensions.width, dimensions.height]
      );

      if (!vectorizingResponse.success) {
        return handleFail(vectorizingResponse.error);
      }
      handlePolling(vectorizingResponse.id);
    },
    download: async (): Promise<void> => {
      set({ loading: true });
      const { displayedImage, fileName } = get();

      if (!displayedImage) {
        fireToast({
          label: 'Image is not found',
          error: true,
          duration: 5000,
        });

        set({ loading: false });
        return;
      }

      await downloadFile(displayedImage, `${fileName}-Kittl.svg`);
      const { dimensions, backgroundRemoved } = get();
      const options: SapDownloadPayload = {
        page: 'vectorizer',
        label: 'svg',
        dimensions: dimensions,
        removeBackground: backgroundRemoved,
      };
      void analytics.track(DOWNLOAD_SAP, options);
      set({ loading: false });
    },
  },
}));
